<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import type { PromotionCode } from "@/rvapi";
import { generateRandomString, inXDays, local2utc } from "@/utils";
import { rotate } from "pn-design-assets/pn-assets/icons";
import { usePromotionStore } from "@/stores/promotions";
const state = ref("loading"); // ready, uploading, result
const promotions = usePromotionStore();
const codetext = ref("");
const codeexpire = ref<string | null>(null);
const cardcount = ref("1");
const codevalue = computed(() => {
  return parseInt(cardcount.value) * (cardPrice.value ?? 25);
});

const cardPrice = ref<number | null>(null);
const coderesult = ref<PromotionCode | null>(null);
const codeerrormsg = ref("");
const valueerrormsg = ref("");
const dateerrormsg = ref("");

const datepickermsg = computed(() => {
  return dateerrormsg.value || "Valid until";
});
const randomize = () => {
  const consonants = "ABCDFGHJKLMNPQRSTVWXZ";
  const digits = "0123456789";
  const text: string =
    generateRandomString(3, consonants) + generateRandomString(2, digits);
  codetext.value = text;
};
const isCodeValid = (a: any) => {
  if (!a.target.value) {
    codeerrormsg.value = "Code text is required";
    return;
  }
  const validCode = /^[!?_+A-Za-z0-9-]+$/;
  if (!validCode.test(a.target.value)) {
    codeerrormsg.value = "Code contains invalid characters";
    return;
  }
  codeerrormsg.value = "";
};
const isValueValid = (a: any) => {
  const validNumber = /^[1-9][0-9]*$/;
  if (!validNumber.test(a.target.value)) {
    valueerrormsg.value = "Not a valid number";
  } else if (a.target.value.length > 3) {
    valueerrormsg.value = "Are you serious?";
  } else {
    valueerrormsg.value = "";
  }
};
const isDateValid = () => {
  if (!codeexpire.value) {
    dateerrormsg.value = "Invalid date";
    return;
  }
  try {
    local2utc(codeexpire.value, "23:59:00");
  } catch {
    dateerrormsg.value = "Invalid date";
    return;
  }
  if (new Date(codeexpire.value) < new Date()) {
    dateerrormsg.value = "Date must be in the future";
    return;
  }
  dateerrormsg.value = "";
};

const goodwillDateSelected = (date: { detail: { start: string } }) => {
  codeexpire.value = date.detail?.start;
  isDateValid();
};

const reset = () => {
  codeexpire.value = inXDays(180);
  randomize();
  state.value = "ready";
};
onMounted(async () => {
  reset();
  const pricing = await promotions.getPricing();
  cardPrice.value = pricing[0]?.prices?.SE;
});

const createGoodwill = async () => {
  if (!codeexpire.value) {
    throw "invalid date";
  }
  state.value = "uploading";
  try {
    coderesult.value = await promotions.createGoodwill(
      codetext.value.toUpperCase(),
      parseInt(cardcount.value, 10),
      local2utc(codeexpire.value, "23:59:00"),
    );
  } catch (e: any) {
    if (e?.response?.data?.errorCode === "DUPLICATE_CODE") {
      codeerrormsg.value = "This code has already been used";
      state.value = "ready";
      return;
    }
    console.log(e);
    throw e;
  }
  state.value = "result";
};
</script>

<template>
  <pn-spinner size="2" v-show="state === 'uploading' || state === 'loading'" />
  <section v-show="state === 'ready'">
    <p>You can edit any of these values to your liking.</p>
    <p>
      <pn-input
        :error="codeerrormsg"
        v-on:input="isCodeValid"
        v-model="codetext"
        label="Code"
        helpertext=""
      />
    </p>
    <p>
      <pn-button
        small="true"
        :icon="rotate"
        appearance="light"
        v-on:click="randomize"
        >Re-roll</pn-button
      >
      <!-- todo inputvalidate [A-Z0-9]+ -->
    </p>
    <p>
      <pn-date-picker
        @input="isDateValid"
        @dateSelection="goodwillDateSelected"
        :invalid="dateerrormsg !== ''"
        :label-from="datepickermsg"
        :start="codeexpire"
      />
    </p>
    <p>
      <pn-input
        v-on:input="isValueValid"
        :error="valueerrormsg"
        v-model="cardcount"
        label="Free cards"
        helpertext="Enter number to calculate value"
      />
    </p>
    <p class="small">
      The code can be used to buy cards of any format, and can be used for both
      domestic and international mail. Approximate code value:
      {{ codevalue }} SEK
    </p>
    <p>
      <pn-button
        v-show="!codeerrormsg && !valueerrormsg && !dateerrormsg"
        v-on:click="createGoodwill"
      >
        Create
      </pn-button>
    </p>
  </section>
  <section v-if="state === 'result'">
    <pre>
      Created code {{ coderesult?.code }}
      approximate value {{ codevalue }} SEK,
      expires at {{ coderesult?.expireAt?.substring(0, 10) }}
    </pre>
    <pn-button v-on:click="reset">Continue</pn-button>
  </section>
</template>

<style lang="scss" scoped>
.small {
  width: 25%;
  color: #999;
}
</style>
